import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { IntegrationIcon } from '../../../../../../../components/IntegrationIcon'
import { ReactComponent as ZocksIcon } from '../../../../../../../assets/ZocksIcon.svg'
import { getIntegrationDisplayName, IntegrationServiceId, ZOCKS_CONTACT_DETAIL_FIELDS } from '../../../../../../../common/integrations'
import { useCallback, useMemo } from 'react'
import { InputWithTokens } from '../../../../../../../components/InputWithTokens/InputWithTokens'
import { MentionData } from '../../../../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/MentionNode/MentionNode'
import { InputWithTokensElements } from '../../../../../../../components/InputWithTokens/utils'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useGetDescribeQuery } from '../../../../../../../store/integrations/integrations'
import { FormFieldDefinition } from '../../../hooks'

interface MapContactDetailFieldsSectionProps {
  serviceId: IntegrationServiceId
}

export const MapContactDetailFieldsSection: React.FC<MapContactDetailFieldsSectionProps> = ({ serviceId }) => {
  const {
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)

  const contactDetailFields = watch('mappings.contactFields.fields')

  const contactObject = watch('mappings.contact.objectName')
  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    {
      accountId,
      connector: serviceId,
      context: { objectName: contactObject! },
    },
    { skip: !contactObject || !accountId },
  )

  const currentObjectFormFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])

  const onInputWithTokensChange = useCallback(
    (elements: InputWithTokensElements[], zocksKey: string) => {
      const newDescriptors = elements.map((content) => {
        if (content.type === 'mention' && content.mentionData) {
          const mentionData: MentionData = content.mentionData
          const metadata = mentionData.metadata as { valueType?: string; valueFormat?: string } | undefined
          return {
            type: 'field' as const,
            value: mentionData.id,
            valueType: metadata?.valueType ?? null,
            valueFormat: metadata?.valueFormat ?? null,
          }
        } else {
          return {
            type: 'static' as const,
            value: content.content,
          }
        }
      })

      const currentFields = { ...(getValues('mappings.contactFields.fields') || {}) }
      if (newDescriptors.length > 0) {
        currentFields[zocksKey] = newDescriptors
      } else {
        delete currentFields[zocksKey]
      }

      if (Object.keys(currentFields).length === 0) {
        setValue('mappings.contactFields', null, { shouldDirty: true, shouldValidate: true })
        return
      }
      setValue(
        'mappings.contactFields',
        { objectName: getValues('mappings.contact.objectName'), fields: currentFields },
        { shouldDirty: true, shouldValidate: true },
      )
    },
    [setValue, getValues],
  )

  const inputTokenOptions: MentionData[] = useMemo(() => {
    if (!currentObjectFormFieldDefinitions) {
      return []
    }
    return currentObjectFormFieldDefinitions.map((fieldDef) => ({
      id: fieldDef.name,
      label: `${fieldDef.label} (${fieldDef.name})`,
      metadata: {
        valueType: fieldDef.type,
        valueFormat: fieldDef.extraTypeInfo || null,
      },
    }))
  }, [currentObjectFormFieldDefinitions])

  const mapFieldDescriptorToInputWithTokensElements = (fieldDescriptors: unknown[]): InputWithTokensElements[] => {
    if (!fieldDescriptors) {
      return []
    }
    return fieldDescriptors.map((descriptor) => {
      const { type, value } = descriptor as { type?: string; value?: string }
      if (type === 'static') {
        return { type: 'text', content: value } as InputWithTokensElements
      }
      const fieldDefinition = currentObjectFormFieldDefinitions.find((field) => field.name === value)
      if (!fieldDefinition) {
        return { type: 'text', content: value } as InputWithTokensElements
      }
      return {
        type: 'mention',
        content: `@${value}`,
        mentionData: { id: value, label: `${fieldDefinition.label} (${fieldDefinition.name})`, metadata: { ...fieldDefinition } },
      } as InputWithTokensElements
    })
  }

  if (currentObjectFormFieldDefinitions.length === 0 || isDescribeFetching) {
    return <Spinner />
  }

  return (
    <Container className='p-0'>
      <Row className='mb-3'>
        <Col>
          <h4>Map Contact detail fields</h4>
          <p className='zocks-text-gray-600'>
            Optionally match your {getIntegrationDisplayName(serviceId)} fields to Zocks contact detail fields. Fields left empty use the default{' '}
            {getIntegrationDisplayName(serviceId)} fields.
          </p>
        </Col>
      </Row>

      <Row className='mb-2 fw-bold'>
        <Col className='d-flex align-items-center'>
          <IntegrationIcon serviceId={serviceId} width='1rem' height='1rem' fill='black' />
          <span className='ms-2'>{getIntegrationDisplayName(serviceId).toUpperCase()} FIELD</span>
        </Col>
        <Col className='d-flex align-items-center'>
          <ZocksIcon width='1rem' height='1rem' />
          <span className='ms-2'>ZOCKS FIELD</span>
        </Col>
      </Row>

      {ZOCKS_CONTACT_DETAIL_FIELDS.map((mapping) => {
        const fieldDescriptors = contactDetailFields?.[mapping.zocksKey]
        const elements = mapFieldDescriptorToInputWithTokensElements(fieldDescriptors as unknown[])
        const fieldError = errors.mappings?.contactFields?.fields?.[mapping.zocksKey]

        return (
          <Row key={mapping.zocksKey} className='mb-3 align-items-center'>
            <Col>
              <InputWithTokens
                options={inputTokenOptions}
                onChange={(parsedContents) => onInputWithTokensChange(parsedContents, mapping.zocksKey)}
                elements={elements}
              />
              {fieldError ? <div className='invalid-feedback d-block'>{fieldError.message || 'Invalid field'}</div> : null}
            </Col>
            <Col className='d-flex align-items-center'>
              <i className='bi bi-arrow-right mx-3 zocks-text-gray-500 fs-5'></i>
              <span className='fw-medium'>{mapping.label}</span>
            </Col>
          </Row>
        )
      })}
    </Container>
  )
}
